import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Language Switcher pentru Navbar — dropdown RO/EN.
 *
 * 2026-05-15 — Option B foundation: LandingPage + Navbar traduse.
 * Click → schimbă instant (fără reload). Persistă în localStorage (key "brandvis_lang").
 */
export default function LanguageSwitcher({ compact = false, color = 'var(--ink, #0A1428)' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLng = (i18n.language || 'ro').split('-')[0];
  const isEN = currentLng === 'en';

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const change = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem('brandvis_lang', lng); } catch {}
    setOpen(false);
  };

  const options = [
    { code: 'ro', label: 'RO', flag: '🇷🇴', full: 'Română' },
    { code: 'en', label: 'EN', flag: '🇬🇧', full: 'English' },
  ];
  const current = options.find(o => o.code === currentLng) || options[0];

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="Schimbă limba / Change language"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: compact ? '4px 8px' : '6px 12px',
          background: 'transparent',
          border: `1px solid ${color}33`,
          borderRadius: 8,
          color,
          fontFamily: 'DM Mono, monospace',
          fontSize: compact ? 11 : 12,
          fontWeight: 600,
          letterSpacing: 0.5,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${color}10`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: 14 }}>{current.flag}</span>
        <span>{current.label}</span>
        <span style={{ fontSize: 9, opacity: 0.6, marginLeft: 2 }}>▼</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 8,
            padding: 4,
            minWidth: 140,
            boxShadow: '0 10px 30px rgba(15,23,42,0.15)',
            zIndex: 300,
          }}
        >
          {options.map(opt => {
            const active = opt.code === currentLng;
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => change(opt.code)}
                role="menuitem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 10px',
                  background: active ? `${color}10` : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: 'var(--ink, #0A1428)',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 16 }}>{opt.flag}</span>
                <span style={{ flex: 1 }}>{opt.full}</span>
                {active && <span style={{ color: '#2d6a4f', fontSize: 14 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
